import {LIST_INQUEUE_ASSIGNMENT_STATUS} from "~/components/ottopilotDataViewer/listStatusConfig";
import type {KanbanBoardColumnConfig} from "~/components/ottopilotDataViewer/view";
import type {EventColorMap} from "~/components/beatPlanningCalendar/beatPlanningCalendar";
import {
    AmaronCallStatusEnum,
    AmaronEnquiryStatusEnum,
    AmaronEventTypes,
    AmaronLeadStatusEnum,
    AmaronServiceCategoryEnum,
    amaronCallStatuses,
    amaronEnquiryStatuses,
    amaronLeadStatuses,
    amaronServiceCategories,
} from "~/domainModels/workspaceSpecific/amaron";
import {amaronInqueueStatuses, AmaronInqueueStatusEnum} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/amaron/EventContainers";
import type {SharedCalendarColorConfig, SharedCalendarLegendItem} from "@vesta/vos-types/modules/workspaceLevel/domain/SharedCalendarViewConfig";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uniqueUuid} from "@vesta/unique/unique";
import {
    buildCalendarEventTypeColorMapFromAccents,
    calendarAccentToKanbanColumnColors,
    createCalendarGetEventColorFromTokenConfigMap,
    normalizeCalendarStatusLookupKey,
    resolveCalendarEventTypeColorFromTokenConfigMap,
    type CalendarAccentColor,
} from "~/utilities/sharedCalendarConfig";

/** Amaron status color modules that have list / kanban / calendar views in Neptune. */
export type AmaronCalendarColorModule = "enquiry" | "journeyLeads" | "inQueue" | "calls" | "service";

type AmaronStatusColumnSpec = {
    id: Uuid;
    displayName: string;
    accent: CalendarAccentColor;
    possibleValues: ReadonlyArray<string>;
    /** Extra lookup keys for calendar + list status resolution. */
    calendarKeys?: ReadonlyArray<string>;
};

const AMARON_INQUEUE_ASSIGNED_COLUMN_ID = uniqueUuid`75a844f6-4306-435b-b6f3-c72d20084f71`;
const AMARON_INQUEUE_UNASSIGNED_COLUMN_ID = uniqueUuid`3f2e2eb7-8f74-4aa1-a6e1-ff4f87be5e95`;

const AMARON_STATUS_COLUMN_SPECS: Record<AmaronCalendarColorModule, ReadonlyArray<AmaronStatusColumnSpec>> = {
    enquiry: [
        {
            id: AmaronEnquiryStatusEnum.OPEN as Uuid,
            displayName: amaronEnquiryStatuses[AmaronEnquiryStatusEnum.OPEN].displayName,
            accent: "gray",
            possibleValues: [AmaronEnquiryStatusEnum.OPEN],
        },
        {
            id: AmaronEnquiryStatusEnum.ENGAGED as Uuid,
            displayName: amaronEnquiryStatuses[AmaronEnquiryStatusEnum.ENGAGED].displayName,
            accent: "blue",
            possibleValues: [AmaronEnquiryStatusEnum.ENGAGED],
        },
        {
            id: AmaronEnquiryStatusEnum.NON_ENGAGED as Uuid,
            displayName: amaronEnquiryStatuses[AmaronEnquiryStatusEnum.NON_ENGAGED].displayName,
            accent: "purple",
            possibleValues: [AmaronEnquiryStatusEnum.NON_ENGAGED],
            calendarKeys: ["Non Engaged"],
        },
    ],
    journeyLeads: [
        {
            id: AmaronLeadStatusEnum.LIVE as Uuid,
            displayName: amaronLeadStatuses[AmaronLeadStatusEnum.LIVE].displayName,
            accent: "blue",
            possibleValues: [AmaronLeadStatusEnum.LIVE],
        },
        {
            id: AmaronLeadStatusEnum.CHURNED as Uuid,
            displayName: amaronLeadStatuses[AmaronLeadStatusEnum.CHURNED].displayName,
            accent: "yellow",
            possibleValues: [AmaronLeadStatusEnum.CHURNED],
        },
        {
            id: AmaronLeadStatusEnum.CONVERTED as Uuid,
            displayName: amaronLeadStatuses[AmaronLeadStatusEnum.CONVERTED].displayName,
            accent: "green",
            possibleValues: [AmaronLeadStatusEnum.CONVERTED],
        },
    ],
    inQueue: [
        {
            id: AMARON_INQUEUE_ASSIGNED_COLUMN_ID,
            displayName: "Assigned",
            accent: "green",
            possibleValues: [LIST_INQUEUE_ASSIGNMENT_STATUS.assigned],
            calendarKeys: [
                AmaronInqueueStatusEnum.ASSIGNED,
                amaronInqueueStatuses[AmaronInqueueStatusEnum.ASSIGNED].id,
                amaronInqueueStatuses[AmaronInqueueStatusEnum.ASSIGNED].displayName,
                "assigned",
                "Assigned",
            ],
        },
        {
            id: AMARON_INQUEUE_UNASSIGNED_COLUMN_ID,
            displayName: "Unassigned",
            accent: "yellow",
            possibleValues: [LIST_INQUEUE_ASSIGNMENT_STATUS.unassigned, ""],
            calendarKeys: [
                AmaronInqueueStatusEnum.UNASSIGNED,
                amaronInqueueStatuses[AmaronInqueueStatusEnum.UNASSIGNED].id,
                amaronInqueueStatuses[AmaronInqueueStatusEnum.UNASSIGNED].displayName,
                "unassigned",
                "Unassigned",
            ],
        },
    ],
    calls: [
        {
            id: AmaronCallStatusEnum.LOGGED as Uuid,
            displayName: amaronCallStatuses[AmaronCallStatusEnum.LOGGED].displayName,
            accent: "blue",
            possibleValues: [AmaronCallStatusEnum.LOGGED],
        },
        {
            id: AmaronCallStatusEnum.SCHEDULED as Uuid,
            displayName: amaronCallStatuses[AmaronCallStatusEnum.SCHEDULED].displayName,
            accent: "yellow",
            possibleValues: [AmaronCallStatusEnum.SCHEDULED],
        },
        {
            id: AmaronCallStatusEnum.CANCELLED as Uuid,
            displayName: amaronCallStatuses[AmaronCallStatusEnum.CANCELLED].displayName,
            accent: "purple",
            possibleValues: [AmaronCallStatusEnum.CANCELLED],
        },
    ],
    service: [
        {
            id: AmaronServiceCategoryEnum.BOOKED as Uuid,
            displayName: amaronServiceCategories[AmaronServiceCategoryEnum.BOOKED].displayName,
            accent: "blue",
            possibleValues: [AmaronServiceCategoryEnum.BOOKED],
        },
        {
            id: AmaronServiceCategoryEnum.RE_SCHEDULED as Uuid,
            displayName: amaronServiceCategories[AmaronServiceCategoryEnum.RE_SCHEDULED].displayName,
            accent: "yellow",
            possibleValues: [AmaronServiceCategoryEnum.RE_SCHEDULED],
            calendarKeys: ["Re Scheduled", "Rescheduled"],
        },
        {
            id: AmaronServiceCategoryEnum.DELIVERED as Uuid,
            displayName: amaronServiceCategories[AmaronServiceCategoryEnum.DELIVERED].displayName,
            accent: "green",
            possibleValues: [AmaronServiceCategoryEnum.DELIVERED],
        },
        {
            id: AmaronServiceCategoryEnum.CANCELLED as Uuid,
            displayName: amaronServiceCategories[AmaronServiceCategoryEnum.CANCELLED].displayName,
            accent: "purple",
            possibleValues: [AmaronServiceCategoryEnum.CANCELLED],
        },
    ],
};

function buildCalendarColorMapFromStatusSpecs(specs: ReadonlyArray<AmaronStatusColumnSpec>): Record<string, SharedCalendarColorConfig> {
    return buildCalendarEventTypeColorMapFromAccents(
        specs.map((spec) => ({
            keys: [
                ...(spec.calendarKeys ?? []),
                String(spec.id),
                spec.displayName,
                ...spec.possibleValues.map(String),
            ],
            accent: spec.accent,
        })),
    );
}

function buildKanbanColumnConfigFromStatusSpecs(specs: ReadonlyArray<AmaronStatusColumnSpec>): Array<KanbanBoardColumnConfig> {
    return specs.map((spec) => {
        const colors = calendarAccentToKanbanColumnColors(spec.accent);
        return {
            id: spec.id,
            displayName: spec.displayName,
            background: colors.background,
            textColor: colors.textColor,
            possibleValues: [...spec.possibleValues],
        };
    });
}

const AMARON_CALENDAR_EVENT_TYPE_COLOR_MAPS: Record<
    AmaronCalendarColorModule,
    Record<string, SharedCalendarColorConfig>
> = {
    enquiry: buildCalendarColorMapFromStatusSpecs(AMARON_STATUS_COLUMN_SPECS.enquiry),
    journeyLeads: buildCalendarColorMapFromStatusSpecs(AMARON_STATUS_COLUMN_SPECS.journeyLeads),
    inQueue: buildCalendarColorMapFromStatusSpecs(AMARON_STATUS_COLUMN_SPECS.inQueue),
    calls: buildCalendarColorMapFromStatusSpecs(AMARON_STATUS_COLUMN_SPECS.calls),
    service: buildCalendarColorMapFromStatusSpecs(AMARON_STATUS_COLUMN_SPECS.service),
};

const AMARON_ARCHIVED_STATUS_IDS = new Set<string>([
    String(AmaronLeadStatusEnum.ARCHIVED),
    String(AmaronEnquiryStatusEnum.ARCHIVE),
    String(AmaronCallStatusEnum.ARCHIVE),
    String(AmaronServiceCategoryEnum.ARCHIVE),
]);

const AMARON_STATUS_ACCENT_LOOKUP: Record<string, CalendarAccentColor> = (() => {
    const lookup: Record<string, CalendarAccentColor> = {};
    for (const specs of Object.values(AMARON_STATUS_COLUMN_SPECS)) {
        for (const spec of specs) {
            lookup[String(spec.id)] = spec.accent;
            for (const value of spec.possibleValues) {
                lookup[String(value)] = spec.accent;
            }
            for (const key of spec.calendarKeys ?? []) {
                lookup[String(key)] = spec.accent;
                lookup[normalizeCalendarStatusLookupKey(String(key))] = spec.accent;
            }
        }
    }
    return lookup;
})();

/** Sidebar status bar icon color — matches kanban header / list pill accent text colors. */
export function getAmaronStatusNavIconColor(statusId: string): string {
    if (AMARON_ARCHIVED_STATUS_IDS.has(String(statusId))) {
        return calendarAccentToKanbanColumnColors("red").textColor;
    }
    const accent = AMARON_STATUS_ACCENT_LOOKUP[String(statusId)];
    if (accent === undefined) {
        return calendarAccentToKanbanColumnColors("blue").textColor;
    }
    return calendarAccentToKanbanColumnColors(accent).textColor;
}

export function getAmaronStatusKanbanColumnConfig(module: AmaronCalendarColorModule): Array<KanbanBoardColumnConfig> {
    // Resolve Atlaskit tokens at call time — module-load resolution returns empty strings before theme init.
    return buildKanbanColumnConfigFromStatusSpecs(AMARON_STATUS_COLUMN_SPECS[module]);
}

const AMARON_EVENT_TEMPLATE_COLOR_MODULE: Partial<Record<Uuid, AmaronCalendarColorModule>> = {
    [AmaronEventTypes.Enquiry as Uuid]: "enquiry",
    [AmaronEventTypes.Calls as Uuid]: "calls",
    [AmaronEventTypes.Service as Uuid]: "service",
};

const AMARON_CALENDAR_LEGEND_ITEMS: Record<AmaronCalendarColorModule, Array<SharedCalendarLegendItem>> = {
    enquiry: [
        {type: AmaronEnquiryStatusEnum.OPEN, label: "Open"},
        {type: AmaronEnquiryStatusEnum.ENGAGED, label: "Engaged"},
        {type: AmaronEnquiryStatusEnum.NON_ENGAGED, label: "Non-Engaged"},
    ],
    journeyLeads: [
        {type: AmaronLeadStatusEnum.LIVE, label: "Live"},
        {type: AmaronLeadStatusEnum.CHURNED, label: "Churned"},
        {type: AmaronLeadStatusEnum.CONVERTED, label: "Converted"},
    ],
    inQueue: [
        {type: AmaronInqueueStatusEnum.ASSIGNED, label: "Assigned"},
        {type: AmaronInqueueStatusEnum.UNASSIGNED, label: "Unassigned"},
    ],
    calls: [
        {type: AmaronCallStatusEnum.LOGGED, label: "Logged"},
        {type: AmaronCallStatusEnum.SCHEDULED, label: "Scheduled"},
        {type: AmaronCallStatusEnum.CANCELLED, label: "Cancelled"},
    ],
    service: [
        {type: AmaronServiceCategoryEnum.BOOKED, label: "Booked"},
        {type: AmaronServiceCategoryEnum.RE_SCHEDULED, label: "Re-Scheduled"},
        {type: AmaronServiceCategoryEnum.DELIVERED, label: "Delivered"},
        {type: AmaronServiceCategoryEnum.CANCELLED, label: "Cancelled"},
    ],
};

export function getAmaronJourneyLeadsKanbanColumnConfig(): Array<KanbanBoardColumnConfig> {
    return getAmaronStatusKanbanColumnConfig("journeyLeads");
}

export function getAmaronInQueueKanbanColumnConfig(): Array<KanbanBoardColumnConfig> {
    return getAmaronStatusKanbanColumnConfig("inQueue");
}

export function resolveAmaronEventStatusColorModule(eventTemplateId: Uuid): AmaronCalendarColorModule | undefined {
    return AMARON_EVENT_TEMPLATE_COLOR_MODULE[eventTemplateId];
}

export function getAmaronEventKanbanColumnConfig(eventTemplateId: Uuid): Array<KanbanBoardColumnConfig> | null {
    const module = resolveAmaronEventStatusColorModule(eventTemplateId);
    if (module === undefined) {
        return null;
    }
    return getAmaronStatusKanbanColumnConfig(module);
}

export function resolveAmaronCalendarEventColor(module: AmaronCalendarColorModule, type: string | undefined): EventColorMap {
    return resolveCalendarEventTypeColorFromTokenConfigMap(type, AMARON_CALENDAR_EVENT_TYPE_COLOR_MAPS[module]);
}

export function createAmaronCalendarGetEventColor(
    module: AmaronCalendarColorModule,
    fallback?: (type: string | undefined) => EventColorMap,
): (type: string | undefined) => EventColorMap {
    return createCalendarGetEventColorFromTokenConfigMap(AMARON_CALENDAR_EVENT_TYPE_COLOR_MAPS[module], fallback);
}

export function getAmaronCalendarLegendItems(module: AmaronCalendarColorModule): Array<SharedCalendarLegendItem> {
    return AMARON_CALENDAR_LEGEND_ITEMS[module];
}

export function resolveAmaronCalendarEventTypeLabel(module: AmaronCalendarColorModule, type: string): string {
    const legendItem = AMARON_CALENDAR_LEGEND_ITEMS[module].find(
        (item) =>
            item.type === type ||
            normalizeCalendarStatusLookupKey(item.type) === normalizeCalendarStatusLookupKey(type) ||
            normalizeCalendarStatusLookupKey(item.label) === normalizeCalendarStatusLookupKey(type),
    );
    return legendItem?.label ?? type;
}
